import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil, finalize, forkJoin, of, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { WorkCenterLabelService } from '../../services/work-center-label.service';
import { WorkCenterLabel } from '../../models/work-center-label.model';
import { PlantFilterSelectionChange } from '../../shared/components/plant-filters/plant-filters.component';
import { FILTER_VALUE } from '../../shared/constants/filters.constants';
import { ContextService } from '../../core/services/context.service';
import { RuleService } from '../../services/rule.service';
import { RulesGroupedByWorkCenterLabel } from '../../models/rules-grouped-by-work-center-label.model';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { createDefaultTableState } from '../../../utils/table.utils';
import { FullTableState } from '../../store/common/app.reducer';
import { cloneDeep } from 'lodash';
import { SelectItem } from 'primeng/api';
import { criticalityOptions as CRITICALITY_OPTIONS } from '../../shared/constants/criticality-option.constans';
import { stageOptions as STAGE_OPTIONS } from '../../shared/constants/stage-options.constant';
import { ruleStateOptions as RULE_STATE_OPTIONS } from '../../shared/constants/rule-state-options.constans';
import { AlarmStatusOptions as ALARM_STATUS_OPTIONS } from '../../shared/constants/alarm-status.constants';
import { DEFAULT_OPTION } from '../../shared/constants/default-options.constans';
import {
  getStageLabel,
  getCriticalityLabel,
} from '../../../utils/enum-label.utils';
import { CriticalityLevel } from '../../../enum/criticality-level.enum';
import { RuleDialogService } from '../../shared/components/modify-rule/rule-dialog.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { saveTableLazyLoadEvent } from '../../store/common/app.action';
import { selectSavedLazyLoadEvent } from '../../store/common/app.selectors';

@Component({
  selector: 'app-work-center-view',
  templateUrl: './work-center-view.component.html',
  styleUrl: './work-center-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkCenterViewComponent implements OnInit, OnDestroy {
  @ViewChild('criteriatable') table!: Table;

  selectedPlant: string = FILTER_VALUE.ALL;
  selectedPlannerGroups: string[] = [];
  selectedWorkCenters: string[] = [];
  selectedCriticality: string = FILTER_VALUE.ALL;
  selectedStage: string = FILTER_VALUE.ALL;
  selectedState: string = FILTER_VALUE.HEALTHY;
  selectedStatus: string = FILTER_VALUE.ALL;
  selectedEquipmentRef: string = '';
  pickedDate: Date | null = null;

  labelDialogVisible = false;
  labels: WorkCenterLabel[] = [];
  loadingLabels = false;

  // Table / filter options
  criticalityOptions: SelectItem[] = [
    ...DEFAULT_OPTION,
    ...CRITICALITY_OPTIONS,
  ];
  stageOptions: SelectItem[] = STAGE_OPTIONS;
  ruleStateOptions: SelectItem[] = RULE_STATE_OPTIONS;
  alarmStatusOptions: SelectItem[] = ALARM_STATUS_OPTIONS;

  // Table state
  fullTableState: FullTableState = createDefaultTableState();
  totalRecords: number = 0;
  loading = false;

  // Grouped rules data
  groupedRules: RulesGroupedByWorkCenterLabel[] = [];

  // Helpers exposed to template
  getStageLabel = getStageLabel;
  getCriticalityLabel = getCriticalityLabel;
  CriticalityLevel = CriticalityLevel;

  private destroy$ = new Subject<void>();
  private dialogRef?: DynamicDialogRef | null;
  private readonly sharedFilterKey = 'rules-overview';

  constructor(
    private workCenterLabelService: WorkCenterLabelService,
    private ruleService: RuleService,
    private contextService: ContextService,
    private ruleDialogService: RuleDialogService,
    private cdr: ChangeDetectorRef,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.selectedPlant = this.contextService.getSelectedPlant();
    this.selectedPlannerGroups = this.contextService.getSelectedPlannerGroups();
    this.selectedWorkCenters = this.contextService.getSelectedWorkCenters();

    // Restore filter values from shared store
    this.store
      .select(selectSavedLazyLoadEvent(this.sharedFilterKey))
      .pipe(take(1))
      .subscribe((savedState) => {
        if (savedState?.filters) {
          const filters = savedState.filters as any;
          if (filters['criticality']?.value != null) {
            this.selectedCriticality = filters['criticality'].value;
          }
          if (filters['alarm']?.value != null) {
            this.selectedStatus = filters['alarm'].value;
          }
          if (filters['ruleStage']?.value != null) {
            this.selectedStage = filters['ruleStage'].value;
          }
          if (filters['state']?.value != null) {
            this.selectedState = filters['state'].value;
          }
          if (filters['latestAlarmStartDate']?.value != null) {
            this.pickedDate = filters['latestAlarmStartDate'].value;
          }
        }

        if (this.hasWorkCenterSelected) {
          this.syncFiltersToTableState();
          this.loadLabels();
        }
      });
  }

  get hasWorkCenterSelected(): boolean {
    return this.selectedWorkCenters.length > 0;
  }

  get effectiveWorkCenterId(): string {
    return this.selectedWorkCenters[0] ?? '';
  }

  onFilterSelectionChange(change: PlantFilterSelectionChange): void {
    this.selectedPlant = change.plant;
    this.selectedPlannerGroups = change.plannerGroups;
    this.selectedWorkCenters = change.workCenters;

    this.syncFiltersToTableState();
    this.saveSharedFilterState();

    if (this.hasWorkCenterSelected) {
      this.loadGroupedRules();
      this.loadLabels();
    } else {
      this.groupedRules = [];
      this.totalRecords = 0;
      this.labels = [];
      this.cdr.markForCheck();
    }
  }

  openLabelDialog(): void {
    this.labelDialogVisible = true;
    this.loadLabels();
  }

  onLabelsChanged(): void {
    this.loadLabels();
    this.loadGroupedRules();
  }

  filter(value: any, field?: string) {
    if (this.table) {
      this.table.filter(value, field || 'global', 'contains');
    }
  }

  applyFilter(value: any) {
    if (this.table) {
      this.table.filter(value, 'latestAlarmStartDate', 'equals');
    }
  }

  loadData(lazyLoadEvent: TableLazyLoadEvent) {
    if (!lazyLoadEvent) return;

    this.fullTableState.first = lazyLoadEvent.first ?? 0;
    this.fullTableState.rows = lazyLoadEvent.rows ?? this.fullTableState.rows;

    if (lazyLoadEvent.sortField !== undefined) {
      this.fullTableState.sortField = lazyLoadEvent.sortField as string;
      this.fullTableState.sortOrder = (lazyLoadEvent.sortOrder as number) ?? 0;
    }

    this.syncFiltersToTableState();
    this.saveSharedFilterState();
    this.loadGroupedRules();
  }

  private loadGroupedRules(): void {
    if (!this.hasWorkCenterSelected) {
      this.groupedRules = [];
      this.totalRecords = 0;
      return;
    }

    this.loading = true;

    const payload = {
      First: this.fullTableState.first ?? 0,
      Rows: this.fullTableState.rows ?? 50,
      SortField: this.fullTableState.sortField ?? null,
      SortOrder: this.fullTableState.sortOrder ?? 0,
      Filters: cloneDeep(this.fullTableState.filters) ?? {},
      GlobalFilter: null,
      Factory: this.selectedPlant ?? '',
      IsOrderedByHealthIndicator: true,
    };

    this.ruleService
      .GetGroupedByWorkCenterLabel(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (groups) => {
          this.groupedRules = groups
            .map((g) => ({
              ...g,
              rules: [...g.rules].sort((r1, r2) =>
                (r1.context ?? '').localeCompare(r2.context ?? '', 'fr', {
                  sensitivity: 'base',
                  numeric: true,
                }),
              ),
            }))
            .sort((a, b) => {
              const labelA = a.workCenterLabel?.label ?? '';
              const labelB = b.workCenterLabel?.label ?? '';
              return labelA.localeCompare(labelB, 'fr', {
                sensitivity: 'base',
                numeric: true,
              });
            });
          this.totalRecords = groups.reduce(
            (sum, g) => sum + g.rules.length,
            0,
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.groupedRules = [];
          this.totalRecords = 0;
          this.cdr.markForCheck();
        },
      });
  }

  private syncFiltersToTableState(): void {
    if (!this.fullTableState.filters) {
      this.fullTableState.filters = {};
    }

    if (this.selectedPlant && this.selectedPlant !== FILTER_VALUE.ALL) {
      this.fullTableState.filters['plant'] = {
        value: this.selectedPlant,
        matchMode: 'contains',
      };
    } else {
      delete this.fullTableState.filters['plant'];
    }

    if (this.selectedWorkCenters.length > 0) {
      this.fullTableState.filters['workCenters'] = {
        value: this.selectedWorkCenters,
        matchMode: 'in',
      };
    } else {
      delete this.fullTableState.filters['workCenters'];
    }

    if (this.selectedPlannerGroups.length > 0) {
      this.fullTableState.filters['plannerGroups'] = {
        value: this.selectedPlannerGroups,
        matchMode: 'in',
      };
    } else {
      delete this.fullTableState.filters['plannerGroups'];
    }

    this.fullTableState.filters['alarm'] = {
      value: this.selectedStatus ?? FILTER_VALUE.ALL,
      matchMode: 'contains',
    };
  }

  private saveSharedFilterState(): void {
    this.store.dispatch(
      saveTableLazyLoadEvent({
        lazyLoadEvent: cloneDeep(this.fullTableState),
        componentName: this.sharedFilterKey,
      }),
    );
  }

  private loadLabels(): void {
    if (!this.hasWorkCenterSelected) return;

    this.loadingLabels = true;
    const requests = this.selectedWorkCenters.map((id) =>
      this.workCenterLabelService.getByWorkCenterId(id),
    );
    forkJoin(requests)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingLabels = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (results: WorkCenterLabel[][]) => {
          const seen = new Set<string>();
          this.labels = results
            .flat()
            .filter((l) => {
              if (seen.has(l.uid)) return false;
              seen.add(l.uid);
              return true;
            })
            .sort((a, b) =>
              a.label.localeCompare(b.label, 'fr', {
                sensitivity: 'base',
                numeric: true,
              }),
            );
          this.cdr.markForCheck();
        },
        error: () => {
          this.labels = [];
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openCreateDialog(): void {
    this.dialogRef = this.ruleDialogService.openCreateRuleDialog(true);
  }

  openSimpleCreateDialog(): void {
    this.dialogRef = this.ruleDialogService.openSimpleCreateRuleDialog();
  }
}
